import { useMemo, useState } from "react";
import FullscreenModal from "../FullscreenModal";
import AppPage from "../AppPage";
import MenuBottomBar, { MenuBarItem } from "../MenuBottomBar";
import SearchComponent from "../SearchComponent";
import { Page } from "../../types";
import { globals, utilities, images } from "../../styles";
import {
  arrow_Icon,
  groups_Icon,
  microphone_Icon,
  plus_Icon,
  trophy_Icon,
} from "../../assets/icons";
import { useNavigate } from "../../hooks/useNavigate";
import styles from "./GroupPage.module.css";
import {
  isSearchResultGroup,
  MemberGroupResponse,
  NonMemberGroupResponse,
  SearchResult,
} from "../../api/types";
import { useCurrentPlayer, useGetPlayers } from "../../hooks/player";
import {
  useCreateGroup,
  useGroup,
  useGroups,
  useJoinGroup,
} from "../../hooks/groups";
import LoadingSpinner from "../LoadingSpinner";

enum FormField {
  GroupName = "groupName",
  JoinCode = "joinCode",
  IsPrivate = "isPrivate",
}

function GroupCard(props: {
  id: string;
  name: string;
  host: string;
  count: string;
  handleClick: () => void;
}) {
  const { id, name, host, count, handleClick } = props;
  return (
    <button id={id} className={styles["group-card"]} onClick={handleClick}>
      <div className={styles["group-card-info"]}>
        <span className={globals["group-card-name"]}>{name}</span>
        <div className={styles["group-card-host"]}>
          <img
            src={microphone_Icon}
            alt=""
            className={images["group-card-host-icon"]}
          />
          <span className={globals["group-card-host-name"]}>{host}</span>
        </div>
      </div>
      <div className={styles["group-card-right"]}>
        <div className={styles["group-card-avatars"]}>
          <img
            src={groups_Icon}
            alt=""
            className={images["group-card-avatar"]}
          />
        </div>
        <span className={globals["group-card-count"]}>{count}</span>
      </div>
    </button>
  );
}

function GroupPage(props: {
  selectedGroupId: string | undefined;
  setSelectedGroupId: (id: string | undefined) => void;
}) {
  const { selectedGroupId, setSelectedGroupId } = props;
  const { navigate } = useNavigate();
  const { data: playerDetails, isPending: isPlayerDetailsLoading } =
    useCurrentPlayer();
  const groupQueries = useGroups(playerDetails?.groupIds ?? []);

  const { mutate: createGroup, isPending: isCreateGroupPending } =
    useCreateGroup();

  const { mutate: joinGroup } = useJoinGroup();

  const isDataPending = useMemo(() => {
    return isPlayerDetailsLoading || groupQueries.some((q) => q.isPending);
  }, [isPlayerDetailsLoading, groupQueries]);

  const groups = useMemo(() => {
    if (isDataPending) return [];
    return groupQueries?.map((q) => q.data).filter((q) => !!q);
  }, [isDataPending, groupQueries]);

  const hostPlayerQueries = useGetPlayers(
    groups
      ?.filter((g) => g && "hostPlayerId" in g)
      .map(
        (g) => (g as MemberGroupResponse | NonMemberGroupResponse).hostPlayerId,
      ) ?? [],
  );
  const hostUsernameById = Object.fromEntries(
    hostPlayerQueries.map((id, i) => [
      id,
      hostPlayerQueries[i]?.data?.username,
    ]),
  ) as { [id: string]: string | undefined };

  const [showGroupSearch, setShowGroupSearch] = useState(false);
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const { data: selectedGroupData, isPending: isSelectedGroupDataLoading } =
    useGroup(selectedGroupId);
  const selectedGroupMembersQueries = useGetPlayers(
    selectedGroupData && "memberIds" in selectedGroupData
      ? selectedGroupData.memberIds
      : [],
  );

  const handleCreateGroup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const groupName = formData.get(FormField.GroupName) as string;
    const isPrivate = formData.get(FormField.IsPrivate) === "on";
    createGroup(
      { name: groupName, isPrivate },
      {
        onSuccess: (response) => {
          setSelectedGroupId(response.groupId);
        },
      },
    );
  };

  const handleJoinGroup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const joinCode = formData.get(FormField.JoinCode) as string;

    if (!selectedGroupId) {
      return;
    }
    joinGroup(
      { groupId: selectedGroupId, joinCode },
      {
        onSuccess: (response) => {
          setSelectedGroupId(response.groupId);
        },
      },
    );
  };

  const handleCreateGroupClicked = () => {
    setShowGroupSearch(false);
    setShowNewGroupModal(true);
  };

  const handleJoinGroupClicked = () => {
    setShowGroupSearch(true);
    setShowNewGroupModal(false);
  };

  const handleCancelJoinGroup = () => {
    setSelectedGroupId(undefined);
  };

  const handleGroupListClicked = (group: SearchResult) => {
    if (!isSearchResultGroup(group)) return;
    setShowGroupSearch(false);
    setSelectedGroupId(group.groupId);
  };

  const handleGroupDetailsClose = () => {
    setSelectedGroupId(undefined);
  };

  const handleJoinFromDetails = () => {
    setSelectedGroupId(selectedGroupId);
  };

  const hostedGroupCards = useMemo(
    () =>
      groups
        .map((g) => {
          if (
            g &&
            "hostPlayerId" in g &&
            g.hostPlayerId === playerDetails?.id &&
            g.isMember
          ) {
            return (
              <GroupCard
                id={g.groupId}
                name={g.name}
                host={playerDetails.username}
                count={`${g.memberCount} / ${g.maxMembers}`}
                handleClick={() => setSelectedGroupId(g.groupId)}
              />
            );
          }
          return null;
        })
        .filter((elem) => !!elem),
    [groups, playerDetails?.id, playerDetails?.username, setSelectedGroupId],
  );
  const memberGroupCards = useMemo(
    () =>
      groups
        .map((g) => {
          if (
            g &&
            "hostPlayerId" in g &&
            g.hostPlayerId !== playerDetails?.id &&
            g.isMember
          ) {
            return (
              <GroupCard
                id={g.groupId}
                name={g.name}
                host={hostUsernameById[g.hostPlayerId] ?? "Loading..."}
                count={`${g.memberCount} / ${g.maxMembers}`}
                handleClick={() => setSelectedGroupId(g.groupId)}
              />
            );
          }
          return null;
        })
        .filter((elem) => !!elem),
    [groups, playerDetails?.id, hostUsernameById, setSelectedGroupId],
  );

  function renderNewGroupModal() {
    return (
      <FullscreenModal
        onClose={() => setShowNewGroupModal(false)}
        className={{ container: styles["group-modal-container"] }}
      >
        <div className={images["group-modal-header-icon-wrap"]}>
          <img
            src={plus_Icon}
            alt=""
            className={images["group-modal-header-plus"]}
          />
          <img
            src={microphone_Icon}
            alt=""
            className={images["group-modal-header-mic"]}
          />
        </div>
        <span className={globals["group-modal-heading"]}>
          Create your own group!
        </span>
        <form
          className={`${globals["app-form"]} ${styles["group-form"]}`}
          onSubmit={handleCreateGroup}
          autoComplete="off"
        >
          <div style={{ margin: "10px 0" }}>
            <div className={styles["group-form-row"]}>
              <span className={styles["group-form-label"]}>Group name:</span>
              <input
                type="text"
                id={FormField.GroupName}
                name={FormField.GroupName}
                required
              />
            </div>

            <div className={styles["group-form-row"]}>
              <span className={styles["group-form-label"]}>Private group?</span>
              <input
                type="checkbox"
                id={FormField.IsPrivate}
                name={FormField.IsPrivate}
              />
            </div>
          </div>
          <div
            className={`${globals["app-form__submission"]} ${utilities["flex-center"]} ${utilities["gap-10"]}`}
            style={{ margin: "10px 0" }}
          >
            <button
              className={globals["lrg-btn"]}
              type="submit"
              disabled={isCreateGroupPending}
            >
              {isCreateGroupPending ? (
                <LoadingSpinner size={45} />
              ) : (
                "Create Group"
              )}
            </button>
            <button
              onClick={handleJoinGroupClicked}
              className={globals["lrg-btn"]}
              type="button"
              disabled={isCreateGroupPending}
            >
              Join a group instead
            </button>
          </div>
        </form>
      </FullscreenModal>
    );
  }

  function renderGroupSearch() {
    return (
      <FullscreenModal onClose={() => setShowGroupSearch(false)}>
        <div className={images["group-modal-header-icon-wrap"]}>
          <img
            src={plus_Icon}
            alt=""
            className={images["group-modal-header-plus"]}
          />
          <img
            src={groups_Icon}
            alt=""
            className={images["group-modal-header-mic"]}
          />
        </div>
        <span className={globals["group-modal-heading"]}>Join a group</span>
        <div className={styles["search-area"]}>
          <SearchComponent
            searchType="group"
            handleResultClicked={handleGroupListClicked}
            resultsClassName={styles["group-results"]}
            resultClassName={styles["group-result-card"]}
          />
          <button
            onClick={handleCreateGroupClicked}
            className={globals["lrg-btn"]}
          >
            Make a group instead
          </button>
        </div>
      </FullscreenModal>
    );
  }

  // This modal is visible if you are a member only!
  function renderGroupDetailsModal() {
    if (
      !selectedGroupData ||
      !("isMember" in selectedGroupData) ||
      !selectedGroupData.isMember
    ) {
      return null;
    }
    return (
      <FullscreenModal onClose={handleGroupDetailsClose}>
        <span className={globals["gd-title"]}>{selectedGroupData.name}</span>
        {selectedGroupData.joinCode && (
          <span className={globals["gd-section-label"]}>
            Join Code: {selectedGroupData.joinCode}
          </span>
        )}

        <div className={styles["gd-section"]}>
          <span className={globals["gd-section-label"]}>Members</span>
          {selectedGroupMembersQueries.map((q) => {
            if (q.isLoading) return <LoadingSpinner />;
            return (
              <div key={q.data?.id} className={styles["gd-member-row"]}>
                {q.data?.id === selectedGroupData.hostPlayerId && (
                  <img
                    src={microphone_Icon}
                    alt=""
                    className={styles["gd-host-icon"]}
                  />
                )}
                <span className={globals["gd-member-name"]}>
                  {q.data?.username}
                </span>
              </div>
            );
          })}
          <span className={globals["gd-total"]}>
            Total Members: {selectedGroupData.memberCount}/{" "}
            {selectedGroupData.maxMembers}
          </span>
        </div>

        <div className={styles["gd-actions"]}>
          <button
            className={styles["gd-leaderboard-btn"]}
            onClick={() => navigate(Page.ExpandedLeaderboard)}
          >
            <img
              src={trophy_Icon}
              alt=""
              style={{
                width: 22,
                height: 22,
                filter: "brightness(0) invert(1)",
              }}
            />
            View Leaderboard
          </button>
          <button
            className={globals["lrg-btn"]}
            onClick={handleJoinFromDetails}
          >
            Join Group
          </button>
        </div>
      </FullscreenModal>
    );
  }

  function renderJoinGroupModal() {
    if (
      !selectedGroupData ||
      !("isMember" in selectedGroupData) ||
      selectedGroupData.isMember
    ) {
      return null;
    }
    return (
      <FullscreenModal onClose={handleCancelJoinGroup}>
        <div className={`${utilities["flex-center"]} ${utilities["flex-gap"]}`}>
          {isSelectedGroupDataLoading && <LoadingSpinner />}
          {selectedGroupData && (
            <form
              className={globals["user-form"]}
              onSubmit={handleJoinGroup}
              autoComplete="off"
            >
              <div className={globals["user-form__group"]}>
                <div className={globals["groups-heading"]}>
                  {selectedGroupData.name}
                </div>
              </div>
              <div className={globals["user-form__group"]}>
                <div className={globals["groups-subheading"]}>
                  {`Member count: ${selectedGroupData.memberCount}`}
                </div>
              </div>
              {selectedGroupData.isPrivate && (
                <div className={globals["user-form__group"]}>
                  <label htmlFor={FormField.JoinCode}>Join code:</label>
                  <input
                    type="text"
                    id={FormField.JoinCode}
                    name={FormField.JoinCode}
                    required
                  />
                </div>
              )}
              <button
                type="submit"
                className={globals["lrg-btn"]}
                style={{ marginTop: "30px" }}
              >
                {`Join Group`}
              </button>
            </form>
          )}
        </div>
      </FullscreenModal>
    );
  }

  return (
    <AppPage
      className={globals["page"]}
      bottomBar={
        <MenuBottomBar
          items={[
            MenuBarItem.Home,
            MenuBarItem.News,
            MenuBarItem.Leaderboards,
            MenuBarItem.LogOut,
          ]}
        />
      }
    >
      <button
        className={`${globals["back-btn"]} ${globals["back-btn--top-left"]}`}
        onClick={() => navigate(Page.Welcome)}
      >
        <img src={arrow_Icon} alt="" />
      </button>
      {showNewGroupModal && renderNewGroupModal()}
      {showGroupSearch && renderGroupSearch()}
      {selectedGroupData &&
        "isMember" in selectedGroupData &&
        selectedGroupData.isMember &&
        renderGroupDetailsModal()}
      {selectedGroupData &&
        "isMember" in selectedGroupData &&
        !selectedGroupData.isMember &&
        renderJoinGroupModal()}
      {isDataPending ? (
        <LoadingSpinner />
      ) : (
        <>
          <span className={globals["groups-heading"]}>Groups</span>
          <div className={styles["scroll-container"]}>
            <>
              {hostedGroupCards.length > 0 ? (
                <>
                  <span className={globals["groups-subheading"]}>
                    Your Hosted Groups
                  </span>
                  {hostedGroupCards}
                </>
              ) : null}

              {memberGroupCards.length > 0 ? (
                <>
                  <span className={globals["groups-subheading"]}>
                    Your Joined Groups
                  </span>
                  {memberGroupCards}
                </>
              ) : null}
            </>
          </div>
          <div className={styles["actions-container"]}>
            <button
              onClick={handleCreateGroupClicked}
              className={globals["sml-icon-btn"]}
            >
              <div className={styles["btn-icon-group"]}>
                <img
                  src={plus_Icon}
                  alt=""
                  className={`${images["btn-inline-icon--plus"]} ${styles["btn-plus-pos--mic"]}`}
                />
                <img
                  src={microphone_Icon}
                  alt=""
                  className={images["btn-inline-icon--mic"]}
                />
              </div>
              Make a group
            </button>
            <button
              onClick={handleJoinGroupClicked}
              className={globals["sml-icon-btn"]}
            >
              <div className={styles["btn-icon-group"]}>
                <img
                  src={plus_Icon}
                  alt=""
                  className={`${images["btn-inline-icon--plus"]} ${styles["btn-plus-pos--group"]}`}
                />
                <img
                  src={groups_Icon}
                  alt=""
                  className={images["btn-inline-icon--mic"]}
                />
              </div>
              Join a group
            </button>
          </div>
        </>
      )}
    </AppPage>
  );
}

export default GroupPage;
